import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API, BACKEND_URL } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Leaf, Users, TrendingUp, Award, ChevronDown, ChevronUp } from 'lucide-react';

function MKDietalAdmin() {
  const navigate = useNavigate();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userEntries, setUserEntries] = useState({});
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchUsuarios();
  }, []);

  const fetchUsuarios = async () => {
    try {
      // Obtener todos los usuarios
      const usersRes = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Obtener estadísticas de MKdietal para cada usuario
      const usuariosConStats = await Promise.all(
        usersRes.data
          .filter(u => u.role === 'miembro' && u.aprobado)
          .map(async (usuario) => {
            try {
              const statsRes = await axios.get(`${API}/mkdietal/admin/user/${usuario.id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              return { ...usuario, mkdietal: statsRes.data };
            } catch (e) {
              return { ...usuario, mkdietal: null };
            }
          })
      );
      
      // Ordenar por racha (de mayor a menor)
      usuariosConStats.sort((a, b) => {
        const rachaA = a.mkdietal?.streak || 0;
        const rachaB = b.mkdietal?.streak || 0;
        return rachaB - rachaA;
      });
      
      setUsuarios(usuariosConStats);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserEntries = async (userId) => {
    if (userEntries[userId]) return;
    
    try {
      const res = await axios.get(`${API}/mkdietal/admin/user/${userId}/entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserEntries(prev => ({ ...prev, [userId]: res.data }));
    } catch (error) {
      console.error('Error fetching entries:', error);
    }
  };

  const toggleExpand = (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
    } else {
      setExpandedUser(userId);
      fetchUserEntries(userId);
    }
  };

  const getColorClass = (color) => {
    switch (color) {
      case 'verde': return 'bg-green-500';
      case 'amarillo': return 'bg-yellow-500';
      case 'rojo': return 'bg-red-500';
      default: return 'bg-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-mk flex items-center justify-center">
        <div className="text-gold">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-mk p-4 pb-20" data-testid="mkdietal-admin-page">
      <div className="max-w-4xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            Volver
          </Button>
        </div>

        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gold flex items-center justify-center gap-2">
            <Leaf className="h-8 w-8" />
            MKdietal - Panel Admin
          </h1>
          <p className="text-gray-400 mt-1">Seguimiento de alimentación del equipo</p>
        </div>

        {/* Resumen general */}
        <Card className="card-mk border-gray-800">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-gold">{usuarios.length}</p>
                <p className="text-xs text-gray-400">Miembros</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-500">
                  {usuarios.filter(u => u.mkdietal?.streak >= 3).length}
                </p>
                <p className="text-xs text-gray-400">En racha (3+)</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-yellow-500">
                  {usuarios.filter(u => u.mkdietal?.total > 0).length}
                </p>
                <p className="text-xs text-gray-400">Activos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Lista de usuarios */}
        <div className="space-y-3">
          {usuarios.map((usuario) => (
            <Card 
              key={usuario.id} 
              className={`card-mk border-gray-800 cursor-pointer transition-all ${
                expandedUser === usuario.id ? 'ring-1 ring-gold' : ''
              }`}
              onClick={() => toggleExpand(usuario.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {usuario.foto_perfil ? (
                      <img
                        src={`${BACKEND_URL}${usuario.foto_perfil}`}
                        alt={usuario.nombre}
                        className="w-12 h-12 rounded-full object-cover border-2 border-gray-700"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gold flex items-center justify-center font-bold text-gray-900">
                        {usuario.nombre?.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div>
                      <p className="text-white font-medium">{usuario.nombre}</p>
                      <div className="flex items-center gap-2 text-xs">
                        {usuario.mkdietal?.streak > 0 ? (
                          <span className="text-green-400 flex items-center gap-1">
                            <TrendingUp className="h-3 w-3" />
                            {usuario.mkdietal.streak} días
                          </span>
                        ) : (
                          <span className="text-gray-500">Sin racha</span>
                        )}
                        {usuario.mkdietal?.badges > 0 && (
                          <span className="text-gold flex items-center gap-1">
                            <Award className="h-3 w-3" />
                            {usuario.mkdietal.badges}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    {/* Mini resumen de colores */}
                    {usuario.mkdietal && (
                      <div className="flex gap-1 text-xs">
                        <span className="px-2 py-1 rounded bg-green-900/50 text-green-400">
                          {usuario.mkdietal.verde || 0}
                        </span>
                        <span className="px-2 py-1 rounded bg-yellow-900/50 text-yellow-400">
                          {usuario.mkdietal.amarillo || 0}
                        </span>
                        <span className="px-2 py-1 rounded bg-red-900/50 text-red-400">
                          {usuario.mkdietal.rojo || 0}
                        </span>
                      </div>
                    )}
                    
                    {expandedUser === usuario.id ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Detalle expandido */}
                {expandedUser === usuario.id && (
                  <div className="mt-4 pt-4 border-t border-gray-800">
                    {userEntries[usuario.id] ? (
                      <div>
                        <p className="text-xs text-gray-400 mb-2">Últimos 14 días:</p>
                        <div className="flex flex-wrap gap-1">
                          {userEntries[usuario.id].slice(0, 14).map((entry, idx) => (
                            <div
                              key={idx}
                              className={`w-8 h-8 rounded flex items-center justify-center text-xs text-white ${getColorClass(entry.color)}`}
                              title={`${entry.fecha}: ${entry.color}`}
                            >
                              {new Date(entry.fecha).getDate()}
                            </div>
                          ))}
                          {userEntries[usuario.id].length === 0 && (
                            <p className="text-gray-500 text-sm">Sin registros</p>
                          )}
                        </div>
                        
                        {usuario.mkdietal?.porcentaje_verde > 0 && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                              <span>Porcentaje verde</span>
                              <span>{usuario.mkdietal.porcentaje_verde}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-gray-700 overflow-hidden">
                              <div 
                                className="h-full bg-green-500 transition-all"
                                style={{ width: `${usuario.mkdietal.porcentaje_verde}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-sm">Cargando...</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}

          {usuarios.length === 0 && (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-gray-700 mb-2" />
              <p className="text-gray-500">No hay miembros activos</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MKDietalAdmin;
